import React, { useState, useRef, useEffect } from "react";
import "../../../../../css/CashflowModeller2/Inputs/Inputs.css";
import "../../../../../css/CashflowModeller2/Inputs/Input2Medium.css";
import ArrowDown from "../Buttons/ArrowDown";
import Dropdown from "../Buttons/Dropdown";
import useClickOutside from "../../Hooks/UseClickOutside";

const sanitizeNumeric = (s) => (s ?? "").replace(/[^0-9.-]/g, "");

// format integer part with commas, keep decimals/trailing "." exactly
const formatWithCommasKeepDecimals = (raw) => {
  if (raw === "" || raw === "-" || raw === "." || raw === "-.") return raw;
  const m = raw.match(/^(-?)(\d*)(\.?)(\d*)$/);
  if (!m) return raw;
  const [, sign, intPart = "", dot = "", decPart = ""] = m;
  const intWithCommas =
    intPart === "" ? "" : intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${intWithCommas}${dot}${decPart}`;
};

const Input2Medium = ({
  isDisabled,
  getStatus,
  label,
  type = "number", // "number" | "per" | "invType"
  currency = false,
  per = false, // when true and type === "number", show % on blur
  value = "",
  options = [],
  onChange = () => {},
  onUnitChange = () => {},
  isDropdownCenter,
  isError,
  readOnly,
  currentUnit,
  placeholderText = "",
}) => {
  const [status, setStatus] = useState("default");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [unit, setUnit] = useState(currentUnit || "Annual");
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef();

  // Keep a raw string (no commas/symbols) for the parent and precise editing
  const [rawEditing, setRawEditing] = useState(() =>
    sanitizeNumeric(value?.toString?.() || "")
  );

  // What we render inside the input (commas while typing)
  const [internalValue, setInternalValue] = useState(() => {
    if (type !== "number") return value?.toString?.() || "";
    return formatWithCommasKeepDecimals(rawEditing);
  });

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  // Keep unit in sync if a unit-bearing value is passed
  useEffect(() => {
    if (!options.length) return;
    const selectedOption = options.find(
      (opt) => opt.value === value?.unit || value?.unit === opt
    );
    if (selectedOption) setUnit(selectedOption.value);
  }, [value, options]);

  // Sync from external `value` -> internal state (don’t clobber mid-typing)
  useEffect(() => {
    const s = value?.toString?.() || "";
    if (type !== "number") {
      if (!isFocused) setInternalValue(s);
      return;
    }
    if (isFocused) return;
    const raw = sanitizeNumeric(s);
    setRawEditing(raw);
    setInternalValue(formatWithCommasKeepDecimals(raw));
  }, [value, type, isFocused]);

  /** Format-as-you-type with commas and preserved decimals; pass raw to parent */
  const handleChange = (e) => {
    const input = e.target.value;

    if (type === "number") {
      const raw = sanitizeNumeric(input);
      const numericRegex = /^-?\d*\.?\d*$/; // allow single dot & leading minus
      if (!numericRegex.test(raw)) return;

      setRawEditing(raw);
      setInternalValue(formatWithCommasKeepDecimals(raw));
      onChange(raw); // parent gets clean raw (no commas/symbols)
      return;
    }

    // non-number inputs (percent selector or type)
    setInternalValue(input);
    onChange(input);
  };

  const handleArrowClick = () => {
    if (!isDisabled) setIsDropdownOpen((prev) => !prev);
  };

  const handleOptionSelect = (opt) => {
    setIsDropdownOpen(false);
    setUnit(opt.value);

    const isATR =
      options.length && options[0]?.label?.includes("%") && type === "per";

    if (isATR || type === "invType") {
      onChange(opt.value); // e.g. ATR "3" or inv type "ISA"
    } else {
      onUnitChange(opt.value);
    }
  };

  /** Display when not focused (adds £/% as needed) */
  const formatDisplay = () => {
    if (type === "invType") return internalValue;
    if (!internalValue) return "";

    if (type !== "number") return internalValue;

    const pretty = formatWithCommasKeepDecimals(rawEditing);

    if (per) return `${pretty}%`;
    if (unit === "Percentage %") return `${pretty}%`;
    if (
      currency === true ||
      unit === "Monthly" ||
      unit === "Annual" ||
      unit === "Per annum"
    )
      return `£${pretty}`;

    return pretty;
  };

  return (
    <div className={label ? "input2-medium input-label-container" : ""}>
      {label && <div className="Label">{label}</div>}

      <div
        className={`input-container ${isDisabled ? "disabled" : ""} ${status} ${
          getStatus || ""
        }  ${isError ? "error" : ""} ${readOnly ? "readOnly" : ""}`}
        ref={dropdownRef}
      >
        <div className="input-unit-wrapper">
          <input
            type="text"
            inputMode={type === "number" ? "decimal" : "text"}
            pattern={type === "number" ? "-?\\d*\\.?\\d*" : undefined}
            placeholder={
              placeholderText ||
              (type === "number"
                ? currency
                  ? "£1,200.00"
                  : "2%"
                : type === "per"
                ? "3%"
                : type === "invType"
                ? label === "Type"
                  ? "ISA"
                  : "William"
                : "Investment")
            }
            value={isFocused ? internalValue : formatDisplay()}
            onChange={handleChange}
            onFocus={() => {
              setStatus("selected");
              setIsFocused(true);
              if (options.length > 0 && type !== "number")
                setIsDropdownOpen(true);
            }}
            onBlur={() => {
              setStatus("default");
              setIsFocused(false);
            }}
            disabled={isDisabled}
            className={`input2-medium input-field Input ${
              isDisabled ? "disabled" : ""
            }`}
            readOnly={options.length > 0 && type !== "number"}
          />

          {options.length > 0 && !readOnly && (
            <div
              className="input-unit-display input-icon-container arrow-icon"
              onClick={handleArrowClick}
            >
              {internalValue && options.some((opt) => opt.value === unit) && (
                <div className="Label">
                  {options.find((opt) => opt.value === unit)?.display}
                </div>
              )}
              <ArrowDown />
            </div>
          )}
        </div>

        {isDropdownOpen && (
          <div
            className={`input-dropdown-panel ${
              isDropdownCenter ? "center-options" : ""
            }`}
          >
            {options.map((opt) => (
              <div key={opt.value} onClick={() => handleOptionSelect(opt)}>
                <Dropdown
                  labelText={opt.label}
                  className={` ${unit === opt.value ? "selected" : ""}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Input2Medium;
